import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, MousePointerClick, Eye, TrendingUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AnalyticsData {
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalConverted: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  recentEmails: Array<{
    id: string;
    lead_name: string;
    template_name: string;
    sent_at: string;
    opened_at: string | null;
    clicked_at: string | null;
    open_count: number;
    click_count: number;
  }>;
}

export const EmailAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch all analytics for user's sent emails
      const { data: analyticsData, error: analyticsError } = await supabase
        .from("email_analytics")
        .select(`
          *,
          scheduled_emails!inner(agent_id, lead_id, template_id),
          leads(name),
          email_templates(template_name)
        `)
        .eq("scheduled_emails.agent_id", user.id)
        .order("sent_at", { ascending: false })
        .limit(10);

      if (analyticsError) throw analyticsError;

      const totalSent = analyticsData?.length || 0;
      const totalOpened = analyticsData?.filter(a => a.opened_at).length || 0;
      const totalClicked = analyticsData?.filter(a => a.clicked_at).length || 0;
      const totalConverted = analyticsData?.filter(a => a.converted_at).length || 0;

      const openRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
      const clickRate = totalSent > 0 ? (totalClicked / totalSent) * 100 : 0;
      const conversionRate = totalSent > 0 ? (totalConverted / totalSent) * 100 : 0;

      const recentEmails = (analyticsData || []).map((a: any) => ({
        id: a.id,
        lead_name: a.leads?.name || "Unknown",
        template_name: a.email_templates?.template_name || "Unknown",
        sent_at: a.sent_at,
        opened_at: a.opened_at,
        clicked_at: a.clicked_at,
        open_count: a.open_count,
        click_count: a.click_count,
      }));

      setAnalytics({
        totalSent,
        totalOpened,
        totalClicked,
        totalConverted,
        openRate,
        clickRate,
        conversionRate,
        recentEmails,
      });
    } catch (error: any) {
      console.error("Error fetching analytics:", error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalSent}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Rate</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.openRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {analytics.totalOpened} opened
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Click Rate</CardTitle>
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.clickRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {analytics.totalClicked} clicked
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.conversionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {analytics.totalConverted} converted
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Email Activity</CardTitle>
          <CardDescription>Performance of your last 10 sent emails</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.recentEmails.map((email) => (
              <div key={email.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                <div className="space-y-1">
                  <p className="font-medium">{email.lead_name}</p>
                  <p className="text-sm text-muted-foreground">{email.template_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Sent {new Date(email.sent_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  {email.opened_at ? (
                    <Badge variant="secondary" className="gap-1">
                      <Eye className="h-3 w-3" />
                      {email.open_count}x
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not opened</Badge>
                  )}
                  {email.clicked_at && (
                    <Badge variant="secondary" className="gap-1">
                      <MousePointerClick className="h-3 w-3" />
                      {email.click_count}x
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};